'use client'

import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { generateUUID } from '../../../libs/uuid'

import useOnceEffect from '../../../hooks/once-effect'
import useUpdateEffect from '../../../hooks/update-effect'
import { faFilter, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Flex, Grid, Popover } from '@radix-ui/themes'

import { useTableContext, useTableStore, useVisibleColumns } from '../../context/table-context'
import {
    StyledAddButton,
    StyledFiltersHeader,
    StyledResetButton,
    StyledShowButton,
} from '../../styled/filter'
import { TFilter } from '../../types'
import { TFilterCollection } from '../../types/table-props'
import { serializeAppliedFilters } from '../table-utils'
import Filter from './filter'
import PinnedFilters, { isPinnedFilterKey, resolvePinnedFilter } from './pinned-filters'

export type TTableFilterRef = {
    filters: () => TFilterCollection
}

type TResetFiltersButtonProps = {
    filters: TFilterCollection
    onReset: () => void
    label: string
}

const ResetFiltersButton = ({ filters, onReset, label }: TResetFiltersButtonProps) => {
    if (Object.values(filters).filter(filter => !filter.hide).length == 0)
        return null

    return (
        <StyledResetButton variant={'surface'} onClick={onReset}>
            {label}
        </StyledResetButton>
    )
}

type TFilterAddButtonProps = {
    onAdd: () => void
    label: string
}

const FilterAddButton = ({ onAdd, label }: TFilterAddButtonProps) => (
    <StyledAddButton variant="soft" color="lime" onClick={onAdd}>
        <FontAwesomeIcon icon={faPlus} /> {label}
    </StyledAddButton>
)

// key scheme kept for compatibility: tables on one page without distinct string keys share it
const getFiltersStorageKey = (saveFilters: boolean | string) => {
    const suffix = typeof saveFilters === 'string' ? `-${saveFilters}` : ''
    return `table-filters-${window.location.pathname}${suffix}`
}

function TableFiltersPopover<T>() {
    const t = useTranslations('table.filters')

    const columns = useVisibleColumns<T>()
    const refreshData = useTableStore<T, 'refreshData'>('refreshData')
    const filters = useTableStore<T, 'filters'>('filters')
    const { saveFilters, store } = useTableContext<T>()
    const [filtersLoaded, setFiltersLoaded] = useState(false)

    const pinnedFilters = columns.filter(column => column.pinnedFilter).map(resolvePinnedFilter)

    const appliedFilters = serializeAppliedFilters(filters)

    const resetFilter = () => {
        store.setFilters(
            Object.entries(filters).reduce(
                (acc, [key, value]) => {
                    if (value.hide)
                        acc[key] = value

                    return acc
                },
                {} as TFilterCollection
            )
        )
    }

    const updateFilter = (key: string, filter: Partial<TFilter>) => {
        const newFilters = { ...filters, [key]: filter }

        // an unfinished filter does not change the query, so it keeps the page as well
        if (serializeAppliedFilters(newFilters) === appliedFilters)
            store.update({ filters: newFilters })
        else
            store.setFilters(newFilters)
    }

    const deleteFilter = (key: string) => {
        const { [key]: _omit, ...newFilters } = filters
        store.setFilters(newFilters)
    }

    const addFilter = () => {
        const key = generateUUID()
        store.setFilters({
            ...filters,
            [key]: {
                column: undefined,
                operator: undefined,
                value: '',
            },
        })
    }

    useUpdateEffect(() => {
        refreshData()
    }, [appliedFilters])

    useOnceEffect(() => {
        if (!saveFilters)
            return

        try {
            const item = sessionStorage.getItem(getFiltersStorageKey(saveFilters))
            if (!item)
                return

            // drop pinned filters of columns that are no longer pinned (or were stored under an older key)
            // and hidden (scope) filters: those always come from the current `defaultFilters`
            const pinnedKeys = pinnedFilters.map(pinned => pinned.key)
            const stored = Object.entries(JSON.parse(item) as TFilterCollection).filter(
                ([key, filter]) => !filter.hide && (!isPinnedFilterKey(key) || pinnedKeys.includes(key))
            )
            const hiddenDefaults = Object.entries(store.get().filters).filter(([, filter]) => filter.hide)

            setFiltersLoaded(true)
            store.setFilters(Object.fromEntries([...hiddenDefaults, ...stored]))
        }
        catch {
            // unreadable or corrupted stored filters; start with none
        }
    })

    useUpdateEffect(() => {
        if (!saveFilters)
            return

        try {
            const visibleFilters = Object.fromEntries(
                Object.entries(filters).filter(([, filter]) => !filter.hide)
            )
            sessionStorage.setItem(getFiltersStorageKey(saveFilters), JSON.stringify(visibleFilters))
        }
        catch {
            // private mode or quota exceeded; filters just will not be remembered
        }
    }, [filters])

    if (columns.length == 0)
        return null

    return (
        <Popover.Root>
            <StyledFiltersHeader>
                {columns.filter(column => !column.pinnedFilter).length > 0 && (
                    <Popover.Trigger>
                        <StyledShowButton variant="soft">
                            <FontAwesomeIcon icon={faFilter} />
                        </StyledShowButton>
                    </Popover.Trigger>
                )}
                <PinnedFilters
                    key={filtersLoaded ? '1' : '0'}
                    filters={filters}
                    deleteFilter={deleteFilter}
                    updateFilter={updateFilter}
                    pinnedFilters={pinnedFilters}
                />
                <ResetFiltersButton
                    filters={filters}
                    onReset={resetFilter}
                    label={t('clear-filter-button')}
                />
            </StyledFiltersHeader>

            <Popover.Content sideOffset={10} minWidth={'25rem'}>
                <Flex gap="2" direction="column" maxHeight="15rem">
                    <FilterAddButton onAdd={addFilter} label={t('add-filter-button')} />

                    {Object.keys(filters).length > 0 && (
                        <Grid columns="1fr 1fr 2fr min-content" gap="1">
                            {Object.entries(filters)
                                .filter(
                                    ([key, filter]) => !filter.hide && !isPinnedFilterKey(key)
                                )
                                .map(([key, filter]) => (
                                    <Filter
                                        key={key}
                                        filter={filter}
                                        columns={columns.filter(column => !column.pinnedFilter)}
                                        updateFilter={(filter: Partial<TFilter>) =>
                                            updateFilter(key, filter)
                                        }
                                        deleteFilter={() => deleteFilter(key)}
                                    />
                                ))}
                        </Grid>
                    )}
                </Flex>
            </Popover.Content>
        </Popover.Root>
    )
}

export default TableFiltersPopover
